import '../App.css';
import * as React from 'react';
import { useEffect, useLayoutEffect } from 'react';

/** Material UI Packages. */
import { LicenseInfo } from '@mui/x-license-pro';

/** Router packages. */
import { BrowserRouter as Router, Route, Routes } from 'react-router-dom';

/** Auth0 authenticator Packages. */
import { useAuth0 } from '@auth0/auth0-react';

/** HTTP Client Liabrary. */
import axios from 'axios';

/** JWT Decoder */
import { jwtDecode } from 'jwt-decode';

/** Internal Components */
import { callAPI } from 'shared/api/callApi';
import useUserRole from 'shared/hooks/useUserRole';
import RenderComponent from './router/RenderComponent';
import LoginPage from './auth/LoginPage';
import ErrorPage from './auth/ErrorPage';
import InactivePage from './auth/InactivePage';

/** Load secrete keys */
const muiKey = process.env.REACT_APP_MUI_KEY;
const auth0Key = process.env.REACT_APP_AUTH0_AUTHORIZATION_API;
const awsAPI = process.env.REACT_APP_DEFAULT_API;

/** Mui Premium license key. */
LicenseInfo.setLicenseKey(muiKey);

function App() {
    /** Destructuring values from useAuth0 hook. */
    const { getAccessTokenSilently, logout, isAuthenticated, user } = useAuth0();
    const userRole = useUserRole();

    /**
     * Get token value from Auth0.
     * Fetch user information via a GET request to the API endpoint.
     **/
    const getUser = async () => {
        try {
            const token = await getAccessTokenSilently({
                authorizationParams: {
                    audience: auth0Key,
                },
            });

            // Decode token and check expiration
            let decodedToken;
            try {
                decodedToken = jwtDecode(token);
            } catch (decodeError) {
                console.error('Invalid token:', decodeError);
                logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                    },
                });
                return;
            }

            const tokenExpiry = decodedToken.exp * 1000;
            const tokenExpiryTime = new Date(tokenExpiry);

            const resp = await axios.get(awsAPI, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
                params: {
                    type: 'sign',
                    email: user.email,
                    first: user.given_name,
                    last: user.family_name,
                    role: userRole ? JSON.stringify(userRole) : null,
                    component: '',
                },
            });
            sessionStorage.setItem('exp', tokenExpiryTime);
            return resp;
        } catch (e) {
            console.log(e);
            if (e.error === 'missing_refresh_token' || e.error === 'invalid_grant') {
                console.log(e);
                logout({
                    logoutParams: {
                        returnTo: window.location.origin,
                    },
                });
            }
        }
    };

    /** Hook to update userRole to the database. */
    useEffect(() => {
        if (isAuthenticated && user) {
            getUser()
                .then((response) => {
                    callAPI({
                        method: 'post',
                        module: 'signup',
                        component: '',
                        getAccessTokenSilently,
                        email: user.email,
                        obj: JSON.stringify(userRole),
                        onSuccess: (res) => {
                            console.log('User data has been successfully updated.');
                        },
                        onError: (err) => {
                            console.error('UserRole update error:', err);
                        },
                    });
                })
                .catch((error) => {
                    console.log('User data update has been failed.');
                });
            return;
        }
    }, [isAuthenticated, user]);

    /** Set document title. */
    useLayoutEffect(() => {
        document.title = 'Operation System | Insight Logistics ';
    }, []);

    return (
        <Router>
            <Routes>
                <Route path="/" element={isAuthenticated ? <RenderComponent opdashboard={true} /> : <LoginPage />} />
                <Route
                    path="/opdashboard"
                    element={isAuthenticated ? <RenderComponent opdashboard={true} /> : <LoginPage />}
                />
                <Route
                    path="/shipments"
                    element={isAuthenticated ? <RenderComponent shipments={true} /> : <LoginPage />}
                />
                <Route
                    path="/dataentry"
                    element={isAuthenticated ? <RenderComponent dataentry={true} /> : <LoginPage />}
                />
                <Route
                    path="/dutycalculator"
                    element={isAuthenticated ? <RenderComponent dutycalculator={true} /> : <LoginPage />}
                />
                <Route
                    path="/quotearchive"
                    element={isAuthenticated ? <RenderComponent quotearchive={true} /> : <LoginPage />}
                />
                <Route
                    path="/guideline"
                    element={isAuthenticated ? <RenderComponent guideline={true} /> : <LoginPage />}
                />
                <Route
                    path="/analytics"
                    element={isAuthenticated ? <RenderComponent analytics={true} /> : <LoginPage />}
                />
                <Route
                    path="/dataexport"
                    element={isAuthenticated ? <RenderComponent dataexport={true} /> : <LoginPage />}
                />
                <Route
                    path="/accounting"
                    element={isAuthenticated ? <RenderComponent accounting={true} /> : <LoginPage />}
                />
                <Route
                    path="/taskboard"
                    element={isAuthenticated ? <RenderComponent taskboard={true} /> : <LoginPage />}
                />
                <Route
                    path="/clientsetting"
                    element={isAuthenticated ? <RenderComponent clientsetting={true} /> : <LoginPage />}
                />
                <Route
                    path="/performance"
                    element={isAuthenticated ? <RenderComponent performance={true} /> : <LoginPage />}
                />
                <Route
                    path="/useractivity"
                    element={isAuthenticated ? <RenderComponent useractivity={true} /> : <LoginPage />}
                />
                <Route
                    path="/ratemanagement"
                    element={isAuthenticated ? <RenderComponent ratemanagement={true} /> : <LoginPage />}
                />
                <Route
                    path="/shipmentmonitoring"
                    element={isAuthenticated ? <RenderComponent shipmentmonitoring={true} /> : <LoginPage />}
                />
                <Route
                    path="/myprofile"
                    element={isAuthenticated ? <RenderComponent myprofile={true} /> : <LoginPage />}
                />
                <Route path="/inactive" element={<InactivePage />} />
                <Route path="*" element={<ErrorPage />} />
            </Routes>
        </Router>
    );
}

export default App;
